/**
 * TanStack's `onlineManager`, fed by the device's network instead of the
 * browser's `online`/`offline` events.
 *
 * ── WHY THE DEFAULT IS WRONG HERE ──────────────────────────────────────────
 * The default listens only to `window` `online`/`offline`. A WebView on gym
 * Wi-Fi reports "online" while every request fails, so a query or mutation
 * FAILS instead of pausing — and mutations default to `retry: 0`, so the write
 * is simply gone. `@capacitor/network` (through `src/native`) is the better
 * signal on the device, and on the web it wraps the same browser events, so
 * nothing is lost there.
 *
 * The outbox runner (`outbox-runner.ts`) reads this same manager, so "online"
 * means one thing across the app.
 */

/** What this needs of TanStack's `OnlineManager`. */
export interface OnlineSink {
  setEventListener(
    setup: (setOnline: (online: boolean) => void) => (() => void) | undefined
  ): void;
  setOnline(online: boolean): void;
}

/** What this needs of `src/native`'s network functions. */
export interface NetworkSource {
  getNetworkState(): Promise<{ readonly connected: boolean }>;
  onNetworkChange(
    handler: (state: { readonly connected: boolean }) => void
  ): () => void;
}

/**
 * Wire `sink` to `source`, and seed it from `getNetworkState()`.
 *
 * The seed is asynchronous; a change event that arrives before it resolves is
 * newer, so the seed is dropped rather than overwriting it. Returns the
 * teardown, which puts back a no-op listener (the manager keeps whatever it
 * was last told).
 */
export function wireOnline(
  sink: OnlineSink,
  source: NetworkSource
): () => void {
  let heardEvent = false;
  let active = true;
  sink.setEventListener((setOnline) =>
    source.onNetworkChange((state) => {
      heardEvent = true;
      setOnline(state.connected);
    })
  );
  source
    .getNetworkState()
    .then((state) => {
      if (active && !heardEvent) {
        sink.setOnline(state.connected);
      }
    })
    .catch((error: unknown) => {
      // No answer is not "offline": keep the manager's current value.
      console.warn("[data] network state unavailable", error);
    });
  return () => {
    active = false;
    sink.setEventListener(() => undefined);
  };
}
